'use client';

import { type FormEvent, useEffect, useState } from 'react';
import {
  ActionIcon,
  Button,
  Input,
  Modal,
  Text,
  Textarea,
  Title,
} from 'rizzui';
import { PiCheckCircleBold, PiXBold } from 'react-icons/pi';
import type { PublicShipment } from '@/server/api';
import {
  PUBLIC_SHIPMENT_AVAILABILITY,
  type PublicShipmentAvailability,
} from './available-load-state';

// Shared with the bid form so a carrier does not have to re-enter their public
// contact details when they decide to book a different listed load.
const CONTACT_CACHE_KEY = 'shipsmart:available-loads-bid-contact:v1';

type BookingContact = {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
};

type BookingForm = BookingContact & {
  message: string;
};

type BookingFieldErrors = Partial<Record<keyof BookingForm, string>>;

const EMPTY_CONTACT: BookingContact = {
  fullName: '',
  email: '',
  phone: '',
  companyName: '',
};

function readCachedContact(): BookingContact {
  if (typeof window === 'undefined') return EMPTY_CONTACT;

  try {
    const cached = JSON.parse(
      window.localStorage.getItem(CONTACT_CACHE_KEY) ?? '{}'
    ) as Partial<BookingContact>;

    return {
      fullName: typeof cached.fullName === 'string' ? cached.fullName : '',
      email: typeof cached.email === 'string' ? cached.email : '',
      phone: typeof cached.phone === 'string' ? cached.phone : '',
      companyName:
        typeof cached.companyName === 'string' ? cached.companyName : '',
    };
  } catch {
    return EMPTY_CONTACT;
  }
}

function cacheContact(contact: BookingContact) {
  window.localStorage.setItem(CONTACT_CACHE_KEY, JSON.stringify(contact));
}

function formatLocation(location: PublicShipment['pickup']) {
  return [location.city, location.state].filter(Boolean).join(', ') || ', ';
}

function formatPrice(value: number | null) {
  return value === null
    ? 'Call for rate'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
}

function validateBookingForm(form: BookingForm): BookingFieldErrors {
  const errors: BookingFieldErrors = {};
  const phoneDigits = form.phone.replace(/\D/g, '');

  if (form.fullName.trim().split(/\s+/).length < 2) {
    errors.fullName = 'Enter your first and last name.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (phoneDigits.length < 7) {
    errors.phone = 'Enter a valid phone number.';
  }
  if (!form.companyName.trim()) {
    errors.companyName = 'Enter your company name.';
  }

  return errors;
}

type BookingSubmittedEvent = {
  pro: number;
  availability: PublicShipmentAvailability;
};

type AvailableLoadBookingModalProps = {
  load: PublicShipment | null;
  onClose: () => void;
  onBookingSubmitted: (event: BookingSubmittedEvent) => void | Promise<void>;
};

export function AvailableLoadBookingModal({
  load,
  onClose,
  onBookingSubmitted,
}: AvailableLoadBookingModalProps) {
  const [form, setForm] = useState<BookingForm>({
    message: '',
    ...EMPTY_CONTACT,
  });
  const [fieldErrors, setFieldErrors] = useState<BookingFieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!load) return;

    setForm({ message: '', ...readCachedContact() });
    setFieldErrors({});
    setSubmitError('');
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, [load]);

  const updateField = <Key extends keyof BookingForm>(
    key: Key,
    value: BookingForm[Key]
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setSubmitError('');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!load || load.price === null || isSubmitting) return;

    const errors = validateBookingForm(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      cacheContact({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        companyName: form.companyName.trim(),
      });
      await onBookingSubmitted({
        pro: load.pro,
        availability: PUBLIC_SHIPMENT_AVAILABILITY.BOOK_PENDING,
      });
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'The booking request could not be submitted.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={load !== null}
      onClose={onClose}
      customSize="920px"
      rounded="xl"
      overlayClassName="bg-[#06162b]/55 backdrop-blur-[3px]"
      containerClassName="available-load-booking-modal !w-[calc(100vw-2rem)] overflow-hidden border border-white/10 bg-white p-0 shadow-[0_32px_90px_rgba(4,20,40,0.32)] sm:!w-[calc(100vw-3rem)] lg:!w-[920px]"
      className="z-[10020]"
    >
      <header className="available-load-booking-modal__header flex items-start justify-between gap-4 border-b border-[#e5ebf2] bg-[#f7fbff] px-5 py-5 sm:px-6">
        <div className="flex min-w-0 items-start gap-3.5">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#087ac0] text-white shadow-[0_8px_22px_rgba(8,122,192,0.2)]">
            <PiCheckCircleBold className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <Title
              as="h2"
              className="text-xl font-semibold tracking-[-0.035em] text-[#081e37]"
            >
              Book this demo load
            </Title>
            <Text className="mt-1 text-sm leading-5 text-[#68778a]">
              Try the Book Now flow without changing live load data.
            </Text>
          </div>
        </div>
        <ActionIcon
          type="button"
          variant="text"
          rounded="full"
          aria-label="Close booking form"
          onClick={onClose}
          className="available-load-booking-modal__close shrink-0 text-[#526174] hover:bg-[#eaf2f9]"
        >
          <PiXBold className="h-5 w-5" aria-hidden="true" />
        </ActionIcon>
      </header>

      {isSubmitted ? (
        <div className="available-load-booking-modal__success px-5 py-8 text-center sm:px-8 sm:py-10">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#e9f8f0] text-[#148354]">
            <PiCheckCircleBold className="h-8 w-8" aria-hidden="true" />
          </span>
          <Title
            as="h3"
            className="mt-5 text-2xl font-semibold tracking-[-0.04em] text-[#081e37]"
          >
            Demo request submitted
          </Title>
          <Text className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#68778a]">
            Load #{load?.pro} now displays the demo hold state. This request is
            stored only in the page and does not reach the live API.
          </Text>
          <Button
            type="button"
            onClick={onClose}
            className="mt-7 bg-[#081e37] text-white hover:bg-[#123556]"
          >
            Close
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="max-h-[min(70dvh,680px)] overflow-y-auto px-5 py-5 sm:px-6 sm:py-6 lg:max-h-none lg:overflow-visible">
            {load ? (
              <div className="available-load-booking-modal__load-summary mb-5 grid grid-cols-[1fr_auto] gap-4 rounded-2xl border border-[#dbe6f0] bg-[#f7fbff] p-4">
                <div className="min-w-0">
                  <Text className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8190a2]">
                    Load #{load.pro}
                  </Text>
                  <Text className="mt-1 truncate text-sm font-semibold text-[#081e37] sm:text-base">
                    {formatLocation(load.pickup)} →{' '}
                    {formatLocation(load.destination)}
                  </Text>
                </div>
                <div className="text-right">
                  <Text className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8190a2]">
                    Book now price
                  </Text>
                  <Text className="mt-1 text-base font-bold text-[#087ac0]">
                    {formatPrice(load.price)}
                  </Text>
                </div>
              </div>
            ) : null}

            <div className="available-load-booking-modal__notice mb-5 rounded-xl border border-[#c9e5f7] bg-[#f2f9fd] px-4 py-3">
              <Text className="text-sm font-semibold text-[#075f96]">
                Demo mode: this does not change a live load.
              </Text>
              <Text className="mt-1 text-sm leading-5 text-[#526f86]">
                Submit this form to see how a $5,000 Book Now request moves to
                the Hold — pending approval state.
              </Text>
            </div>

            <div className="available-load-booking-modal__contact space-y-4 border-t border-[#e5ebf2] pt-4">
              <Text className="text-xs font-bold uppercase tracking-[0.09em] text-[#8190a2]">
                Carrier contact details
              </Text>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Full name"
                  value={form.fullName}
                  onChange={(event) =>
                    updateField('fullName', event.target.value)
                  }
                  placeholder="First and last name"
                  autoComplete="name"
                  inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                  error={fieldErrors.fullName}
                />
                <Input
                  label="Company name"
                  value={form.companyName}
                  onChange={(event) =>
                    updateField('companyName', event.target.value)
                  }
                  placeholder="Transport company"
                  autoComplete="organization"
                  inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                  error={fieldErrors.companyName}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  type="email"
                  label="Email"
                  value={form.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  placeholder="name@company.com"
                  autoComplete="email"
                  inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                  error={fieldErrors.email}
                />
                <Input
                  type="tel"
                  label="Phone number"
                  value={form.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  placeholder="(555) 000-0000"
                  autoComplete="tel"
                  inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                  error={fieldErrors.phone}
                />
              </div>
              <Textarea
                label="Message (optional)"
                value={form.message}
                onChange={(event) => updateField('message', event.target.value)}
                placeholder="Truck availability or other details"
                textareaClassName="min-h-[92px] rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
              />
            </div>

            {submitError ? (
              <Text className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm leading-5 text-red-700">
                {submitError}
              </Text>
            ) : null}
          </div>

          <footer className="available-load-booking-modal__footer flex items-center justify-between gap-3 border-t border-[#e5ebf2] bg-white px-5 py-4 sm:px-6">
            <Text className="hidden max-w-[270px] text-xs leading-5 text-[#8190a2] sm:block">
              Your contact details will be remembered on this device.
            </Text>
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="available-load-booking-modal__secondary-action border-[#ccd7e3] text-[#081e37]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting || load?.price === null}
                className="available-load-booking-modal__submit bg-[#087ac0] text-white hover:bg-[#066aa8]"
              >
                Submit demo request
              </Button>
            </div>
          </footer>
        </form>
      )}
    </Modal>
  );
}
